import * as L from "leaflet";
import * as React from "react";
import { forceRenderStyles, style } from "typestyle";
import "leaflet/dist/leaflet.css";
import { IBoundLayer } from "../../map/BoundLayer";

import GeolocationLayer from "../../map/reusableLayers/GeolocationLayer/GeolocationLayer";
import FlightMarkersLayer from "./layers/FlightMarkersLayer";
import NavigationLayer from "../../map/reusableLayers/NavigationLayer/NavigationLayer";
import SiteBoundariesLayer from "../../map/reusableLayers/SiteBoundariesLayer";

import { createMap } from "../../map/createMap";
import { ReactReduxContext } from "react-redux";
import { getSiteState } from "../../redux/rootReducer";
import { IGcpAppStore } from "../../redux/store";
import FlightMarkersNavigator from "./layers/FlightMarkersNavigator";
import WorldLayer from "../../map/reusableLayers/WorldLayer/WorldLayer";
import ElevationMapLayer from "../../map/reusableLayers/PrescanLayer/ElevationMapLayer";
import OrtophotoLayer from "../../map/reusableLayers/PrescanLayer/OrtophotoLayer";

const mapCSS: string = style({
  height: "100%",
  position: "relative",
  width: "100%",
});

interface ILeafletMapContainerProps {
  store: IGcpAppStore;
}
class ModePilotFlightMarkersLeafletMapContainer extends React.Component<
  ILeafletMapContainerProps,
  {}
> {
  private map: L.Map;
  private mapElRef = React.createRef<HTMLDivElement>();
  private layers: IBoundLayer[];
  private unsubscribeFromStore: () => void;

  public componentDidMount() {
    // Typestyle uses requestAnimationFrame before put styles into head
    // so style() call is asynchronous. We need to ensure that all map styles are rendered
    // by calling forceRenderStyles
    forceRenderStyles();
    this.initLeafletMap();
    setTimeout(() => {
      const siteState = getSiteState(this.props.store.getState());
      this.map.fitBounds(L.geoJSON(siteState.data.boundary).getBounds());
    }, 10);
  }

  public componentWillUnmount() {
    this.unsubscribeFromStore();
    this.layers.forEach((layer) => this.map.removeLayer(layer));
    this.layers = [];
    (window as any).map = null;
    (window as any).layers = null;
  }

  public render() {
    return <div className={mapCSS} ref={this.mapElRef} />;
  }

  private updateLayers = (): void => {
    const state = this.props.store.getState();
    const action = state.lastDispatchedAction;
    setTimeout(() => {
      this.layers.forEach((layer) => {
        layer.update(action);
      });
    }, 0);
  };

  private initLeafletMap = () => {
    this.map = createMap(this.mapElRef.current);

    this.unsubscribeFromStore = this.props.store.subscribe(this.updateLayers);

    // The order of layers in this array affects their zIndex,
    // the last layer is on top. Note that you should implement zIndex feature
    // in your layer by yourself and be aware of leaflet default order
    // http://leafletjs.com/reference-1.3.0.html#map-pane
    this.layers = [
      WorldLayer,
      ElevationMapLayer,
      OrtophotoLayer,
      SiteBoundariesLayer,
      GeolocationLayer,
      FlightMarkersLayer,
      NavigationLayer,
      FlightMarkersNavigator,
    ].map((Layer, zIndex) => {
      const layer = new Layer({
        dispatch: this.props.store.dispatch,
        getState: this.props.store.getState,
        zIndex,
      });
      this.map.addLayer(layer);
      return layer;
    });

    // for debug purposes
    (window as any).map = this.map;
    (window as any).layers = this.layers;

    this.updateLayers();
  };
}

export default function () {
  return (
    <ReactReduxContext.Consumer>
      {({ store }) => (
        <ModePilotFlightMarkersLeafletMapContainer store={store} />
      )}
    </ReactReduxContext.Consumer>
  );
}
